// Multi-pedigree-tabs — workspace reducer (pure).
//
// Owns the set of open pedigrees, their tab order, which is active, and which
// (single) one is the transient "preview". Pure and side-effect free: the caller
// supplies `sessionId` and `now` on the actions that create/activate sessions, so
// the reducer never touches Date.now()/random and is fully unit-testable and
// resume-safe.
//
// Session shape (one open pedigree instance):
//   { sessionId, familyId, title, mode: 'preview'|'pinned',
//     lock: 'none'|'self'|'other', lastActiveAt: number,
//     viewState: object|null }
//
// There is deliberately no "unsaved" flag. A pedigree has no save action — the
// chart autosaves and record saves live in the person panel — so a tab is never
// in an unsaved state. `lock` is the real per-tab status, and the tab strip
// reads it directly (TGCLOUD-1665).
//
// Invariants the reducer maintains:
//   - At most one session with mode 'preview' (previewSessionId points to it).
//   - One familyId is open at most once (opening an already-open family just
//     activates its existing tab — no duplicates).
//   - activeSessionId is always either null or a real session id.

// TGCLOUD-1669 Phase 6 — a session is no longer always a family.
//
// The strip now holds two kinds of thing, so "which tab is this?" can no longer
// be answered with a familyId. It is answered with a SUBJECT KEY: the identity
// of the thing the tab is showing. That keeps the invariant that matters —
// one subject is open at most once — which re-keying everything to sessionId
// would have thrown away, letting the same family open twice.
export const SESSION_KIND = Object.freeze({
  pedigree: 'pedigree',
  person: 'person'
});

/**
 * The identity of what a tab shows. Namespaced by kind, so a person tab and
 * their family's pedigree tab are two different subjects and can both be open.
 */
export const subjectKeyFor = ({ kind, familyId, memberId } = {}) =>
  kind === SESSION_KIND.person
    ? memberId
      ? `person:${memberId}`
      : null
    : familyId
    ? `pedigree:${familyId}`
    : null;

export const WORKSPACE_ACTIONS = Object.freeze({
  OPEN: 'workspace/open',
  SET_ACTIVE: 'workspace/setActive',
  PIN: 'workspace/pin',
  MARK_EDITED: 'workspace/markEdited',
  SET_LOCK: 'workspace/setLock',
  UPDATE_VIEW_STATE: 'workspace/updateViewState',
  RENAME: 'workspace/rename',
  SET_PROBAND_NAME: 'workspace/setProbandName',
  REQUEST_RELEASE: 'workspace/requestRelease',
  MARK_INACCESSIBLE: 'workspace/markInaccessible',
  CLOSE: 'workspace/close',
  CLOSE_OTHERS: 'workspace/closeOthers',
  CLOSE_ALL: 'workspace/closeAll',
  REORDER: 'workspace/reorder',
  RESTORE: 'workspace/restore'
});

export const initialWorkspaceState = Object.freeze({
  sessions: [],
  activeSessionId: null,
  previewSessionId: null
});

const makeSession = ({
  sessionId,
  kind = SESSION_KIND.pedigree,
  familyId,
  memberId,
  title,
  mode,
  now
}) => ({
  sessionId,
  kind,
  // A person tab still carries its familyId: the chart it belongs to, used for
  // the "Open pedigree" action and for scoping. It is no longer the identity.
  familyId,
  memberId: memberId || null,
  subjectKey: subjectKeyFor({ kind, familyId, memberId }),
  title: title || memberId || familyId,
  // Proband display name, filled once the family loads (for the tab tooltip).
  probandName: '',
  mode,
  lock: 'none',
  inaccessible: false,
  // Bumped by a manual "Release lock" tab action; the instance watches it and
  // releases its edit lock (works for a background tab without switching to it).
  releaseNonce: 0,
  lastActiveAt: now || 0,
  viewState: null
});

const indexById = (sessions, sessionId) =>
  sessions.findIndex((s) => s.sessionId === sessionId);

const indexByFamily = (sessions, familyId) =>
  sessions.findIndex(
    (s) =>
      s.familyId === familyId &&
      (s.kind || SESSION_KIND.pedigree) === SESSION_KIND.pedigree
  );

const indexBySubject = (sessions, subjectKey) =>
  sessions.findIndex((s) => s.subjectKey === subjectKey);

// RENAME, SET_PROBAND_NAME and REQUEST_RELEASE all address a PEDIGREE by its
// familyId. A person tab carries the same familyId — it is the chart the person
// belongs to — so an unguarded `s.familyId === familyId` would rename a person's
// tab, or hand back an edit lock it never held, when the pedigree was meant.
//
// MARK_INACCESSIBLE deliberately does NOT use this: it reports a fact about the
// FAMILY, not about one tab. See the case below.
const isPedigreeFor = (s, familyId) =>
  s.familyId === familyId &&
  (s.kind || SESSION_KIND.pedigree) === SESSION_KIND.pedigree;

// After a close, pick the next active session: prefer the left neighbour of the
// closed one, else the new right neighbour, else the preview, else null.
const pickNeighbour = (sessions, closedIndex) => {
  if (sessions.length === 0) return null;
  const leftward = sessions[closedIndex - 1];
  if (leftward) return leftward.sessionId;
  const rightward = sessions[closedIndex]; // element that shifted into the slot
  if (rightward) return rightward.sessionId;
  return sessions[sessions.length - 1].sessionId;
};

export function workspaceReducer(state = initialWorkspaceState, action) {
  switch (action?.type) {
    case WORKSPACE_ACTIONS.OPEN: {
      const {
        kind = SESSION_KIND.pedigree,
        familyId,
        memberId,
        title,
        sessionId,
        now,
        activate = true,
        pinned = false
      } = action;
      const subjectKey = subjectKeyFor({ kind, familyId, memberId });
      if (!subjectKey || !sessionId) return state;

      // Already open (preview or pinned): activate it, refresh recency, never
      // duplicate. If it was a preview and we're opening as pinned, promote it.
      // Deduped on the SUBJECT, so opening a person whose pedigree is already
      // open adds a tab rather than activating the pedigree's.
      const existingIdx = indexBySubject(state.sessions, subjectKey);
      if (existingIdx !== -1) {
        const existing = state.sessions[existingIdx];
        const promoted =
          pinned && existing.mode === 'preview'
            ? { ...existing, mode: 'pinned' }
            : existing;
        const sessions = state.sessions.map((s, i) =>
          i === existingIdx
            ? { ...promoted, lastActiveAt: now ?? s.lastActiveAt }
            : s
        );
        return {
          ...state,
          sessions,
          activeSessionId: activate
            ? existing.sessionId
            : state.activeSessionId,
          previewSessionId:
            promoted.mode === 'pinned' &&
            state.previewSessionId === existing.sessionId
              ? null
              : state.previewSessionId
        };
      }

      const newSession = makeSession({
        sessionId,
        kind,
        familyId,
        memberId,
        title,
        mode: pinned ? 'pinned' : 'preview',
        now
      });

      // Opening a NEW preview replaces the current preview IN PLACE (so the tab
      // doesn't jump); opening pinned (or when there's no preview) appends.
      //
      // TGCLOUD-1669 — the preview slot is now PER KIND. It used to be one slot
      // for the whole strip, which was right when every tab was a pedigree.
      // With person tabs it would mean single-clicking a node evicted the
      // preview pedigree you were clicking IN: the chart would close under you.
      // A previewed person replaces a previewed person, a previewed pedigree
      // replaces a previewed pedigree.
      const previewOfKind = state.sessions.find(
        (x) =>
          x.mode === 'preview' && (x.kind || SESSION_KIND.pedigree) === kind
      );
      if (!pinned && previewOfKind) {
        const previewIdx = indexById(state.sessions, previewOfKind.sessionId);
        if (previewIdx !== -1) {
          const sessions = state.sessions.map((s, i) =>
            i === previewIdx ? newSession : s
          );
          return {
            ...state,
            sessions,
            activeSessionId: activate ? sessionId : state.activeSessionId,
            previewSessionId: sessionId
          };
        }
      }

      return {
        ...state,
        sessions: [...state.sessions, newSession],
        activeSessionId: activate ? sessionId : state.activeSessionId,
        previewSessionId: pinned ? state.previewSessionId : sessionId
      };
    }

    case WORKSPACE_ACTIONS.SET_ACTIVE: {
      const { sessionId, now } = action;
      if (indexById(state.sessions, sessionId) === -1) return state;
      return {
        ...state,
        activeSessionId: sessionId,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId
            ? { ...s, lastActiveAt: now ?? s.lastActiveAt }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.PIN: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, mode: 'pinned' } : s
        ),
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    // The user edited the pedigree: auto-pin it, so a preview tab can't be
    // silently recycled out from under their working context. ONE effect, not
    // two — this used to also raise a `hasUnsavedChanges` flag that nothing ever
    // cleared, which drove a bogus close prompt for the rest of the session
    // (TGCLOUD-1665). Whether the tab holds the edit lock is tracked by SET_LOCK
    // and self-heals on release; "edited" needs no separate memory.
    case WORKSPACE_ACTIONS.MARK_EDITED: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      // No-op when already pinned — a repeat edit must not churn a new state
      // object (and a new context value) on every keystroke-driven autosave.
      if (state.sessions[idx].mode === 'pinned') return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, mode: 'pinned' } : s
        ),
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    case WORKSPACE_ACTIONS.SET_LOCK: {
      const { sessionId, lock } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      if (!['none', 'self', 'other'].includes(lock)) return state;
      // No-op when the lock badge is already this value — avoids churning a new
      // state object (and a new context value) on every badge re-sync.
      if (state.sessions[idx].lock === lock) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, lock } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.UPDATE_VIEW_STATE: {
      const { sessionId, patch } = action;
      if (indexById(state.sessions, sessionId) === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId
            ? { ...s, viewState: { ...(s.viewState || {}), ...(patch || {}) } }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.RENAME: {
      const { familyId, title } = action;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          isPedigreeFor(s, familyId) ? { ...s, title } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.SET_PROBAND_NAME: {
      const { familyId, probandName } = action;
      const idx = indexByFamily(state.sessions, familyId);
      if (idx === -1) return state;
      // No-op when unchanged — the source effect re-runs on every family load.
      if (state.sessions[idx].probandName === (probandName || '')) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          isPedigreeFor(s, familyId)
            ? { ...s, probandName: probandName || '' }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.REQUEST_RELEASE: {
      const { familyId } = action;
      const idx = indexByFamily(state.sessions, familyId);
      if (idx === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          isPedigreeFor(s, familyId)
            ? { ...s, releaseNonce: (s.releaseNonce || 0) + 1 }
            : s
        )
      };
    }

    // MARK_INACCESSIBLE is the one of the four familyId-addressed actions that
    // is NOT about a tab: it reports that the FAMILY could not be loaded — it
    // has been merged, deleted, or moved out of this user's group — and every
    // tab that depends on that family is dead, whatever kind it is.
    //
    // THE DEFECT THIS FIXES. It went through `isPedigreeFor`, borrowed from
    // RENAME and friends, which match pedigree sessions only. A PERSON tab
    // fetches the same family through the same code path and reports the same
    // failure, and the flag never landed on it: no "(unavailable)" badge in the
    // strip, no close/retry affordance, and `shouldFetchFamily` never latched,
    // so the tab sat on a blank pending record and re-fetched (and re-failed)
    // on every visit to the pedigree route. The retry loop is the same one
    // TGCLOUD-1618 was raised for, surviving at the person-tab seam.
    //
    // `sessionId` narrows it to a single tab when a caller knows which one it
    // is; without it the whole family is flagged, which is the honest reading
    // of a family-level load failure.
    case WORKSPACE_ACTIONS.MARK_INACCESSIBLE: {
      const { familyId, sessionId } = action;
      if (!sessionId && !familyId) return state;
      const affects = (s) =>
        sessionId ? s.sessionId === sessionId : s.familyId === familyId;
      // No-op when nothing changes, so a repeated failure report does not churn
      // a new state object (and a new context value) for every open tab.
      if (!state.sessions.some((s) => affects(s) && !s.inaccessible)) {
        return state;
      }
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          affects(s) ? { ...s, inaccessible: true, lock: 'none' } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.CLOSE: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      const sessions = state.sessions.filter((s) => s.sessionId !== sessionId);
      const activeSessionId =
        state.activeSessionId === sessionId
          ? pickNeighbour(sessions, idx)
          : state.activeSessionId;
      return {
        ...state,
        sessions,
        activeSessionId,
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    case WORKSPACE_ACTIONS.CLOSE_OTHERS: {
      const { sessionId } = action;
      const keep = state.sessions.find((s) => s.sessionId === sessionId);
      if (!keep) return state;
      return {
        ...state,
        sessions: [keep],
        activeSessionId: sessionId,
        previewSessionId:
          state.previewSessionId === sessionId ? sessionId : null
      };
    }

    case WORKSPACE_ACTIONS.CLOSE_ALL:
      return { ...initialWorkspaceState };

    case WORKSPACE_ACTIONS.REORDER: {
      const { order } = action;
      if (!Array.isArray(order)) return state;
      // Reorder by an explicit list of session ids; ignore ids that don't exist
      // and append any session the list forgot (defensive against stale input).
      const byId = new Map(state.sessions.map((s) => [s.sessionId, s]));
      const reordered = order.map((id) => byId.get(id)).filter(Boolean);
      const missing = state.sessions.filter(
        (s) => !order.includes(s.sessionId)
      );
      const sessions = [...reordered, ...missing];
      if (sessions.length !== state.sessions.length) return state;
      return { ...state, sessions };
    }

    case WORKSPACE_ACTIONS.RESTORE: {
      const { sessions, activeSessionId, previewSessionId } = action;
      if (!Array.isArray(sessions)) return state;
      const ids = new Set(sessions.map((s) => s.sessionId));
      return {
        sessions,
        activeSessionId: ids.has(activeSessionId) ? activeSessionId : null,
        previewSessionId: ids.has(previewSessionId) ? previewSessionId : null
      };
    }

    default:
      return state;
  }
}

// ---- Action creators (keep the impure bits — id/timestamp — at the edge) ----

export const openPedigreeAction = ({
  familyId,
  title,
  sessionId,
  now,
  activate = true,
  pinned = false
}) => ({
  type: WORKSPACE_ACTIONS.OPEN,
  kind: SESSION_KIND.pedigree,
  familyId,
  title,
  sessionId,
  now,
  activate,
  pinned
});

// TGCLOUD-1669 — a person tab. `familyId` is the chart the person belongs to,
// carried so the tab can offer "Open pedigree" and so scoping still works; the
// tab's identity is the memberId. A person tab does NOT require its pedigree to
// be open: a pedigree instance is a heavy kept-mounted chart, and forcing one
// open for every patient glanced at from Data Explorer or the switcher would
// cost real memory for a chart nobody asked to see.
export const openPersonAction = ({
  memberId,
  familyId,
  title,
  sessionId,
  now,
  activate = true,
  pinned = false
}) => ({
  type: WORKSPACE_ACTIONS.OPEN,
  kind: SESSION_KIND.person,
  memberId,
  familyId,
  title,
  sessionId,
  now,
  activate,
  pinned
});

export const setActiveAction = (sessionId, now) => ({
  type: WORKSPACE_ACTIONS.SET_ACTIVE,
  sessionId,
  now
});

export const pinSessionAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.PIN,
  sessionId
});

export const markEditedAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.MARK_EDITED,
  sessionId
});

export const setLockAction = (sessionId, lock) => ({
  type: WORKSPACE_ACTIONS.SET_LOCK,
  sessionId,
  lock
});

export const updateViewStateAction = (sessionId, patch) => ({
  type: WORKSPACE_ACTIONS.UPDATE_VIEW_STATE,
  sessionId,
  patch
});

export const renameSessionAction = (familyId, title) => ({
  type: WORKSPACE_ACTIONS.RENAME,
  familyId,
  title
});

export const setProbandNameAction = (familyId, probandName) => ({
  type: WORKSPACE_ACTIONS.SET_PROBAND_NAME,
  familyId,
  probandName
});

export const requestReleaseAction = (familyId) => ({
  type: WORKSPACE_ACTIONS.REQUEST_RELEASE,
  familyId
});

// `sessionId` is optional and narrows the flag to one tab; with only a familyId
// every tab on that family is flagged, person tabs included (see the reducer
// case). The caller in PedigreeChartView passes the familyId it failed to load.
export const markInaccessibleAction = (familyId, sessionId) => ({
  type: WORKSPACE_ACTIONS.MARK_INACCESSIBLE,
  familyId,
  sessionId
});

export const closeSessionAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.CLOSE,
  sessionId
});

export const closeOthersAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.CLOSE_OTHERS,
  sessionId
});

export const closeAllAction = () => ({ type: WORKSPACE_ACTIONS.CLOSE_ALL });

export const reorderTabsAction = (order) => ({
  type: WORKSPACE_ACTIONS.REORDER,
  order
});

export const restoreWorkspaceAction = ({
  sessions,
  activeSessionId,
  previewSessionId
}) => ({
  type: WORKSPACE_ACTIONS.RESTORE,
  sessions,
  activeSessionId,
  previewSessionId
});

// ---- Selectors ----

export const getActiveSession = (state) =>
  state.sessions.find((s) => s.sessionId === state.activeSessionId) || null;

// Pedigree-only by design: "is this family open" asks about the chart, not
// about whether one of its members happens to have a person tab.
export const getSessionByFamily = (state, familyId) =>
  state.sessions.find((s) => isPedigreeFor(s, familyId)) || null;

export const isFamilyOpen = (state, familyId) =>
  state.sessions.some((s) => isPedigreeFor(s, familyId));

export const getSessionByMember = (state, memberId) =>
  state.sessions.find(
    (s) =>
      s.subjectKey === subjectKeyFor({ kind: SESSION_KIND.person, memberId })
  ) || null;
